// Rebounding (crashing) page: the ownership postgame packet's page 5 and the KPI section's Rebounding view, view only
// (RD4, 2026-10-09). Built as page 3's sibling (TeamKpiPage): its type scale, column-head band, group heads and
// hairline rows. A TEAM CRASHING billboard, then one game's crashing per player, grouped Bigs / Wings / Guards
// like the board, beside Last 5 and Season once POR has played 5 games. Counts come from GET /api/kpi/rebounding/<gameId>; rates are computed here, as
// on the board. Every cell is coloured by its own positional percentile in the reports' seven-bucket scale
// (util/reportColors.ts, 2026-10-10): Game, Last 5 and Season rank against league player-seasons of the player's
// position; a cell under its sample floor is greyed instead. The player-facing board keeps its bands. Groups come from
// the payload (DAGGER only while config.PACKET_OVERRIDES_ON). Above the players, a team billboard (3+ and 4+ Crashers,
// ORB% on 3s) draws the same cells.
import { CSSProperties, useMemo, useRef } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  Footer,
  Masthead,
  Pending,
  useKpiData,
  useKpiReady,
} from "./common";
import { gameMeta, headshotUrl, ord, packetPageLabel } from "./kpiLib";
import {
  belowFloor,
  CellFill,
  Column,
  pctlFill,
  fitRows,
  readVariants,
  SAMPLE_GREY,
  showWindows,
  teamRowHeight,
  VARIANTS_ENABLED,
} from "./reboundingPacket";
import type { WindowHead } from "./common";
import type { GameInfo, KpiGameListItem } from "./types";

const DISP = "var(--pr-disp)";

interface Counts {
  c: number;
  o: number;
  games?: number;
}

type Cell = Counts & { pctl?: number | null };

export interface ReboundingPlayer {
  key: string;
  first: string;
  last: string;
  nbaId: string | null;
  positionGroup: string | null;
  game: Cell;
  last5: Cell;
  season: Cell;
}

interface TeamStat {
  game: Cell;
  last5: Cell;
  season: Cell;
}

export interface ReboundingResponse {
  status: { state: "ready" | "pending"; reason?: string };
  meta: {
    game: GameInfo | null;
    porGamesToDate?: number;
    showWindows?: boolean;
    lastN?: number;
    source?: string;
    groups?: { key: string; label: string }[];
    percentile?: { season: number; minOpps: number };
    teamError?: string | null;
    /** A window's page (/api/kpi/dashboard/rebounding): the lead column is the window, and `contexts` are the columns
     * beside it. A one-game page sends neither: Game leads, Last 5 / Season follow once POR has played 5 games. */
    lead?: { label: string; games: number };
    contexts?: Context[];
  };
  team?: TeamStats | null;
  players: ReboundingPlayer[];
}

const rate = (k: Counts) => (k.o ? k.c / k.o : null);
const pct = (r: number | null) => (r == null ? "—" : `${Math.round(r * 100)}%`);

// One type scale, the Team KPI page's (TeamKpiPage.tsx): big figures 26 / 24, value blocks 17, group heads 16, band
// labels 14, row labels 12.5, samples and column heads 10. Page 5 takes its sizes only from here.
const T = { big: 26, figure: 24, value: 17, group: 16, band: 14, row: 12.5, small: 10 };
const MUTED = "#6b6864";

// Fixed columns, so Game sits in the same place whether or not Last 5 / Season show (7.7in of content width).
const NAME_W = "2.3in";
const GAME_W = "1.75in";
const GAME_BOX = "1.55in";
const WIN_W = "1.75in";
const COLS = (contexts: number) => [NAME_W, GAME_W, ...Array(contexts).fill(WIN_W)].join(" ");

/** The columns beside the lead (one game or a window). */
type Context = "last5" | "season";
const contextHead = (c: Context, lastN: number) => (c === "last5" ? `Last ${lastN}` : "Season");

// ROOM is what the player rows, the team Game boxes and the group heads share once the masthead, the team labels and
// chips, the column heads and the footer are placed (fitRows splits it); measured on the GSW (18 players) and SAC
// renders, with FIT_SPARE left over.
const ROOM = { none: 833, compact: 777, windows: 730 };
const GROUP_PX = 28;
const FIT_SPARE = 14;
const rowHeight = (players: number, groups: number, team: keyof typeof ROOM) =>
  fitRows(ROOM[team] - groups * GROUP_PX - FIT_SPARE, Math.max(players, 1), team !== "none");

type Fill = CellFill | null;

/** A player cell: its percentile's colour, the grey of a thin sample (no percentile then), or none without
 * opportunities. A cell with no percentile to give (no position group) is grey too. */
function playerCell(k: Cell, col: Column): { fill: Fill; pctl: number | null } {
  if (!k.o) return { fill: null, pctl: null };
  if (belowFloor(k.o, col) || k.pctl == null) return { fill: SAMPLE_GREY, pctl: null };
  return { fill: pctlFill(k.pctl), pctl: k.pctl };
}

/** A team cell: its own league percentile, the same scale as the players'. */
const teamFill = (k: Cell): Fill => (!k.o ? null : k.pctl == null ? SAMPLE_GREY : pctlFill(k.pctl));

const EMPTY: CSSProperties = { background: "#f1f0ee", color: "#9a968f", border: "1px dashed #cfccc8" };
const fillStyle = (fill: Fill): CSSProperties =>
  fill ? { background: fill.background, color: fill.text, boxShadow: fill.outline } : EMPTY;

/** The Game box, drawn as the board's Today cell: the row's height, the rate, then counts and percentile inside. The
 * team billboard uses it unchanged. */
function GameBox({ k, fill, pctl, h }: { k: Counts; fill: Fill; pctl: number | null; h: number }) {
  return (
    <div
      style={{
        width: GAME_BOX,
        height: h,
        display: "flex",
        alignItems: "center",
        padding: "0 8px",
        boxSizing: "border-box",
        fontVariantNumeric: "tabular-nums",
        ...fillStyle(fill),
      }}
    >
      <span style={{ fontFamily: DISP, fontWeight: 800, fontSize: h >= 32 ? T.big : T.figure, lineHeight: 1 }}>
        {pct(rate(k))}
      </span>
      <span style={{ marginLeft: "auto", textAlign: "right", lineHeight: 1.1 }}>
        <span style={{ display: "block", fontSize: T.row, fontWeight: 700 }}>
          {k.c}/{k.o}
        </span>
        {pctl != null && <span style={{ display: "block", fontSize: T.small, fontWeight: 700 }}>{ord(pctl)} pct</span>}
      </span>
    </div>
  );
}

/** The Last 5 / Season chip: smaller than the Game box, the same in the player rows and the team billboard. */
function Chip({ k, fill }: { k: Counts; fill: Fill }) {
  return (
    <span
      style={{
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        width: 54,
        height: 22,
        flexShrink: 0,
        fontFamily: DISP,
        fontWeight: 700,
        fontSize: T.band,
        boxSizing: "border-box",
        fontVariantNumeric: "tabular-nums",
        ...fillStyle(fill),
      }}
    >
      {pct(rate(k))}
    </span>
  );
}

/** A player's Last 5 / Season: the chip with its counts beside it. */
function WindowCell({ k, fill, pctl }: { k: Counts; fill: Fill; pctl: number | null }) {
  const sub = pctl != null ? `${ord(pctl)} pct` : k.games != null ? `${k.games} gm` : "";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <Chip k={k} fill={fill} />
      <span style={{ lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
        <span style={{ display: "block", fontSize: T.row, fontWeight: 600, color: "#3a3a3a" }}>
          {k.c}/{k.o}
        </span>
        {sub && <span style={{ display: "block", fontSize: T.small, color: MUTED }}>{sub}</span>}
      </span>
    </div>
  );
}

function Photo({ nbaId, h }: { nbaId: string | null; h: number }) {
  const ph = h - 6;
  const pw = Math.round(ph * 1.35);
  return (
    <span style={{ width: pw, height: ph, flexShrink: 0, background: "#efedeb", overflow: "hidden", display: "block" }}>
      {nbaId && (
        <img
          src={headshotUrl(nbaId)}
          alt=""
          style={{ width: pw, height: ph, objectFit: "cover", objectPosition: "center top", display: "block" }}
        />
      )}
    </span>
  );
}

// Page 3's furniture, so page 5 reads as its sibling: the column-head band and group heads.
const HEAD_BAND: CSSProperties = {
  display: "grid",
  background: "#f5f5f5",
  borderTop: "2px solid #000",
  borderBottom: "2px solid #4c4c4c",
  padding: "3px 4px 2px",
  fontSize: T.small,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#4c4c4c",
};
const SMALL_HEAD: CSSProperties = {
  fontSize: T.small,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: MUTED,
};

function GroupHead({ label, first }: { label: string; first: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: first ? "5px 0 3px" : "9px 0 3px" }}>
      <span
        style={{
          fontFamily: DISP,
          fontWeight: 700,
          fontSize: T.group,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          lineHeight: 1,
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, height: 1.5, background: "#1a1a1a" }} />
    </div>
  );
}

type TeamKey = "crash3" | "crash4" | "orb3";
type TeamStats = Record<TeamKey, TeamStat>;

// Trevor 2026-10-10: 3+ and 4+ Crashers (the Team KPIs' own counts) beside ORB% on 3s
const TEAM_STATS: { key: TeamKey; label: string }[] = [
  { key: "crash3", label: "3+ Crashers" },
  { key: "crash4", label: "4+ Crashers" },
  { key: "orb3", label: "ORB% on 3s" },
];

/** The team billboard (Trevor, 2026-10-09 / 10): its numbers side by side on equal columns, each its Game box with
 * Last 5 and Season stacked beneath and no wider than it, every cell coloured by its own league percentile. */
function TeamCrashing({
  team,
  contexts,
  lastN,
  h,
}: {
  team: TeamStats;
  contexts: Context[];
  lastN: number;
  h: number;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${TEAM_STATS.length}, 1fr)`,
        borderBottom: "1px solid #cfccc8",
        paddingBottom: 8,
      }}
    >
      {TEAM_STATS.map(({ key, label }, i) => {
        const s = team[key];
        return (
          <div
            key={key}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderLeft: i ? "1px solid #cfccc8" : undefined,
            }}
          >
            <div
              style={{
                width: GAME_BOX,
                fontFamily: DISP,
                fontWeight: 700,
                fontSize: T.band,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                lineHeight: 1,
                marginBottom: 5,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </div>
            <GameBox k={s.game} fill={teamFill(s.game)} pctl={s.game.pctl ?? null} h={h} />
            {contexts.length > 0 && (
              <div style={{ width: GAME_BOX, display: "flex", justifyContent: "space-around", marginTop: 6 }}>
                {contexts.map((ctx) => [contextHead(ctx, lastN), s[ctx]] as const).map(([head, c]) => (
                  <div key={head} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <span style={SMALL_HEAD}>{head}</span>
                    <Chip k={c} fill={teamFill(c)} />
                    <span style={{ fontSize: T.small, color: MUTED, whiteSpace: "nowrap", lineHeight: 1 }}>
                      {c.c}/{c.o}
                      {c.pctl != null ? ` · ${ord(c.pctl)}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ReboundingKpiPage({
  data,
  forceWindows = false,
  pageLabel = packetPageLabel("Rebounding", "rebounding"),
  win,
}: {
  data: ReboundingResponse;
  /** Dev only (?windows=1): Last 5 / Season before the 5th game. */
  forceWindows?: boolean;
  pageLabel?: string;
  /** A window's name, dates and record in the masthead (the KPI dashboard's WindowHead). */
  win?: WindowHead | null;
}) {
  const game = data.meta.game!;
  const g = gameMeta(game, data.meta.porGamesToDate);
  const lastN = data.meta.lastN ?? 5;
  const contexts: Context[] =
    data.meta.contexts ??
    (forceWindows || showWindows(data.meta.porGamesToDate) ? ["last5", "season"] : []);
  const windows = contexts.length > 0;
  const lead = data.meta.lead;
  // a window's lead cell is several games: its sample floor is a stretch's, not one game's
  const leadFloor: Column = !lead ? "game" : lead.games <= 10 ? "last5" : "season";
  const groups = useMemo(() => {
    const order = data.meta.groups ?? [
      { key: "bigs", label: "BIGS" },
      { key: "wings", label: "WINGS" },
      { key: "guards", label: "GUARDS" },
    ];
    const known = new Set(order.map((o) => o.key));
    const byGroup = (key: string | null) =>
      data.players
        .filter((p) => (key ? p.positionGroup === key : !p.positionGroup || !known.has(p.positionGroup)))
        .sort((a, b) => (rate(b.game) ?? -1) - (rate(a.game) ?? -1) || b.game.o - a.game.o);
    const out = order.map((o) => ({ ...o, players: byGroup(o.key) }));
    const loose = byGroup(null);
    if (loose.length) out.push({ key: "unassigned", label: "UNASSIGNED", players: loose });
    return out.filter((o) => o.players.length);
  }, [data]);
  const team = data.team ?? null;
  const rowH = rowHeight(data.players.length, groups.length, !team ? "none" : windows ? "windows" : "compact");
  const cols = COLS(contexts.length);

  return (
    <section className="page" data-screen-label="Rebounding">
      <Masthead
        g={g}
        who={pageLabel}
        kicker="Portland Trail Blazers · Rebounding"
        title="Crashing"
        win={win}
      />
      <div style={{ borderTop: "2px solid #000" }} />
      {team && (
        <>
          <GroupHead label="Team crashing" first />
          <TeamCrashing
            team={team}
            contexts={contexts}
            lastN={lastN}
            h={teamRowHeight(rowH)}
          />
        </>
      )}

      <div style={{ ...HEAD_BAND, gridTemplateColumns: cols, marginTop: team ? 10 : 6 }}>
        <span>Player</span>
        <span>{lead ? lead.label : "Game"}</span>
        {contexts.map((c) => (
          <span key={c}>{contextHead(c, lastN)}</span>
        ))}
      </div>
      {groups.map((grp, gi) => (
        <div key={grp.key}>
          <GroupHead label={grp.label} first={gi === 0} />
          {grp.players.map((p) => {
            const gm = playerCell(p.game, leadFloor);
            return (
              <div
                key={p.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: cols,
                  alignItems: "center",
                  height: rowH,
                  padding: "0 4px",
                  borderBottom: "1px solid #e6e4e1",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                  <Photo nbaId={p.nbaId} h={rowH} />
                  <span
                    style={{
                      fontFamily: DISP,
                      fontWeight: 700,
                      fontSize: T.value,
                      lineHeight: 1,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      paddingRight: 8,
                    }}
                  >
                    {p.first} {p.last}
                  </span>
                </span>
                <GameBox k={p.game} fill={gm.fill} pctl={gm.pctl} h={rowH} />
                {contexts.map((c) => {
                  const cell = playerCell(p[c], c);
                  return <WindowCell key={c} k={p[c]} fill={cell.fill} pctl={cell.pctl} />;
                })}
              </div>
            );
          })}
        </div>
      ))}

      <Footer
        left={win ? "Portland Trail Blazers · KPI Dashboard" : undefined}
        right={`Rebounding · ${win ? win.line : g.line}`}
      />
    </section>
  );
}

/** One game's page with the automation contract on its root; `bar` is the screen-only controls above it. */
function ReboundingView({ gameId, pageLabel, bar }: { gameId: string | null; pageLabel?: string; bar: JSX.Element }) {
  const location = useLocation();
  const { forceWindows } = useMemo(() => readVariants(location.search), [location.search]);
  const load = useKpiData<ReboundingResponse>(gameId ? `api/kpi/rebounding/${encodeURIComponent(gameId)}` : null);
  const root = useRef<HTMLDivElement>(null);
  const ready = useKpiReady(root, load as unknown as Parameters<typeof useKpiReady>[1]);
  const data = load.data;
  return (
    <div className="kpi-doc" ref={root} {...ready}>
      <div className="kpi-webbar">
        {bar}
        {VARIANTS_ENABLED && <span className="lbl">Dev: ?windows=1 shows Last 5 / Season before the 5th game</span>}
      </div>
      {load.error && <Pending title="Rebounding" reason={`Could not load: ${load.error}`} />}
      {data?.status?.state === "pending" && <Pending title="Rebounding" reason={data.status.reason} />}
      {data?.status?.state === "ready" && data.meta.game && (
        <ReboundingKpiPage data={data} forceWindows={forceWindows} pageLabel={pageLabel} />
      )}
    </div>
  );
}

const DASHBOARD = (
  <Link to="/kpi/nba" style={{ fontWeight: 700 }}>
    ← KPI dashboard
  </Link>
);

/** /kpi/nba/packet/:gameId/rebounding: the packet's page 5 (update/packet/config.PARTS prints it). */
export default function ReboundingPacketPage() {
  const { gameId = "" } = useParams();
  return <ReboundingView gameId={gameId} bar={DASHBOARD} />;
}

/** /kpi/nba/rebounding[?game=]: the KPI section's view, a game picker over the same page (latest game first). */
export function ReboundingSitePage() {
  const [params, setParams] = useSearchParams();
  const games = useKpiData<{ season: number; games: KpiGameListItem[] }>("api/kpi/games").data?.games;
  const gameId = params.get("game") || games?.[0]?.gameId || null;
  return (
    <ReboundingView
      gameId={gameId}
      pageLabel="Rebounding · Game"
      bar={
        <>
          {DASHBOARD}
          <span className="lbl">Game</span>
          <select
            value={gameId || ""}
            onChange={(e) => setParams({ game: e.target.value }, { replace: true })}
            aria-label="Game"
          >
            {(games || []).map((g) => (
              <option key={g.gameId} value={g.gameId}>
                {g.label} · {g.result} {g.pts}–{g.oppPts}
                {g.seasonType === "preseason" ? " · pre" : ""}
              </option>
            ))}
          </select>
        </>
      }
    />
  );
}
